import {
  BitkitToggletip,
  BitkitTooltip,
  IconArrowRight,
  IconBuildCache,
  IconClock,
  IconCredits,
  IconFlag,
  IconHashtag,
  IconMagicWand,
  IconPull,
  IconPush,
  IconStopwatch,
  IconTag,
  IconTools,
  IconWorkflow,
  IconWorkflowFlow,
  rem,
  useResponsive,
} from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Text } from '@chakra-ui/react/text';
import { forwardRef, useEffect, useState } from 'react';
import { useInterval } from 'usehooks-ts';

import BuildMetaData from '@/components/BuildListItem/BuildMetaData/BuildMetaData';
import {
  Build,
  calculateDuration,
  getBuildStatusColors,
  isBuildRunning,
  isPullRequest,
  sourceBranch,
  targetBranch,
} from '@/models/Build';
import { formatDateTime, formatDuration } from '@/utils/commonFormats';
import { secondsToDurationString } from '@/utils/secondsToDurationString';

const UTILITY_BUILDS_DOCS_URL = 'https://docs.bitrise.io/en/bitrise-ci/run-and-analyze-builds/utility-builds';

type LowerCardContentDetailsProps = { build: Build };
const BuildTagOrBranches = ({ build }: LowerCardContentDetailsProps) => {
  if (!build.tag && !build.branch && !build.pullRequestTargetBranch) {
    return null;
  }

  const statusColors = getBuildStatusColors(build);
  const TriggerTypeIcon = isPullRequest(build) ? IconPull : IconPush;
  if (build.tag) {
    return (
      <BuildMetaData
        display="flex"
        alignItems="center"
        paddingX="12"
        paddingTop="8"
        paddingBottom={rem(10)}
        gap="4"
        overflow="hidden"
        color={statusColors.textColor}
        backgroundColor={statusColors.backgroundColor}
        _groupHover={{ backgroundColor: statusColors.hoverBackgroundColor }}
      >
        <IconTag color={statusColors.textColor} flexShrink={0} size="16" />
        <Text textStyle="body/md/regular" truncate>
          {build.tag}
        </Text>
      </BuildMetaData>
    );
  }
  return (
    <BuildMetaData
      display="flex"
      alignItems="center"
      paddingX="12"
      paddingTop="8"
      paddingBottom={rem(10)}
      gap="4"
      overflow="hidden"
      color={statusColors.textColor}
      backgroundColor={statusColors.backgroundColor}
      _groupHover={{ backgroundColor: statusColors.hoverBackgroundColor }}
    >
      <TriggerTypeIcon color={statusColors.textColor} flexShrink={0} size="16" />
      <>
        {isPullRequest(build) && (
          <Text flexShrink={1} textStyle="body/md/regular" truncate>
            {sourceBranch(build)}
          </Text>
        )}
        {isPullRequest(build) && <IconArrowRight flexShrink={0} size="16" />}
        <Text flexShrink={1} textStyle="body/md/regular" truncate>
          {targetBranch(build)}
        </Text>
      </>
    </BuildMetaData>
  );
};

const BuildWorkflowName = forwardRef<HTMLDivElement, LowerCardContentDetailsProps>(({ build }, ref) => {
  if (!build.triggeredWorkflow && !build.isPipeline) {
    return null;
  }

  const WorkflowIcon = build?.isPipeline ? IconWorkflowFlow : IconWorkflow;
  const tooltipLabel: string = build?.isPipeline ? 'Pipeline' : 'Workflow';

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="flex-start"
      paddingX="12"
      paddingTop="8"
      paddingBottom={rem(10)}
      paddingRight="12"
    >
      <BitkitTooltip text={tooltipLabel}>
        <BuildMetaData
          icon={WorkflowIcon}
          justifyContent="flex-start"
          minWidth={['100%', 'auto']}
          alignItems="center"
          gap="4"
          color="text/secondary"
          ref={ref}
          zIndex={1}
        >
          <Text textStyle="body/md/regular" truncate>
            {build?.triggeredWorkflow}
          </Text>
        </BuildMetaData>
      </BitkitTooltip>
    </Box>
  );
});
BuildWorkflowName.displayName = 'BuildWorkflowName';

const BuildUtilityBuild = ({ build }: LowerCardContentDetailsProps) => {
  if (!build.finishedAt || !build.utility) {
    return null;
  }

  const hasDivider = Boolean(build.triggeredWorkflow || build.isPipeline);

  return (
    <Box
      display="flex"
      alignItems="center"
      position="relative"
      paddingX="12"
      paddingTop="8"
      paddingBottom={rem(10)}
      _before={
        hasDivider
          ? {
              content: '""',
              display: ['none', 'block'],
              position: 'absolute',
              insetInlineStart: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              width: '1',
              height: '20',
              backgroundColor: 'border/minimal',
            }
          : undefined
      }
      _groupHover={hasDivider ? { _before: { backgroundColor: 'border/regular' } } : undefined}
    >
      <BitkitToggletip
        learnMoreTarget="_blank"
        learnMoreUrl={UTILITY_BUILDS_DOCS_URL}
        text="Qualified as a Utility build, so it didn't use up one of your plan's monthly builds."
        wrapTrigger
      >
        <BuildMetaData icon={IconTools} gap="4" color="text/secondary" position="relative" zIndex={1}>
          <Text textStyle="body/md/regular" truncate>
            Utility build
          </Text>
        </BuildMetaData>
      </BitkitToggletip>
    </Box>
  );
};

const BuildStartedAt = forwardRef<HTMLDivElement, LowerCardContentDetailsProps>(({ build }, ref) => {
  if (!build?.triggeredAt) {
    return null;
  }

  return (
    <BitkitTooltip text="Build started at">
      <BuildMetaData icon={IconClock} color="text/secondary" ref={ref} zIndex={1}>
        <Text textStyle="body/md/regular" lineHeight="16px">
          <time dateTime={build.triggeredAt} style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatDateTime(build.triggeredAt, { hideYear: true, hideSeconds: true })}
          </time>
        </Text>
      </BuildMetaData>
    </BitkitTooltip>
  );
});
BuildStartedAt.displayName = 'BuildStartedAt';

const BuildDuration = forwardRef<HTMLDivElement, LowerCardContentDetailsProps>(({ build }, ref) => {
  const [duration, setDuration] = useState(() => (build && calculateDuration(build)) || 0);
  const isUpdating = build && isBuildRunning(build);

  useEffect(() => {
    if (build) {
      setDuration(calculateDuration(build));
    }
  }, [build, isUpdating]);

  useInterval(
    () => {
      if (build) {
        setDuration(calculateDuration(build));
      }
    },
    isUpdating ? 1000 : null,
  );

  if (duration === 0) {
    return null;
  }

  return (
    <BitkitTooltip text="Duration">
      <BuildMetaData icon={IconStopwatch} color="text/secondary" minWidth="32" ref={ref} zIndex={1}>
        <Text textStyle="body/md/regular" lineHeight="16px">
          <time dateTime={secondsToDurationString(duration)} style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatDuration(duration)}
          </time>
        </Text>
      </BuildMetaData>
    </BitkitTooltip>
  );
});
BuildDuration.displayName = 'BuildDuration';

const BuildCredits = forwardRef<HTMLDivElement, LowerCardContentDetailsProps>(({ build }, ref) => {
  if (!build.creditCost) {
    return null;
  }

  return (
    <BitkitTooltip text="Credit cost">
      <BuildMetaData icon={IconCredits} color="text/secondary" ref={ref} zIndex={1}>
        {build.creditCost}
      </BuildMetaData>
    </BitkitTooltip>
  );
});
BuildCredits.displayName = 'BuildCredits';

const BuildNumber = forwardRef<HTMLDivElement, LowerCardContentDetailsProps>(({ build }, ref) => {
  if (!build.buildNumber) {
    return null;
  }

  return (
    <BitkitTooltip text="Build number">
      <BuildMetaData icon={IconHashtag} color="text/secondary" gap="0" minWidth="0" ref={ref} zIndex={1}>
        {build.buildNumber}
      </BuildMetaData>
    </BitkitTooltip>
  );
});
BuildNumber.displayName = 'BuildNumber';

const BuildPriority = forwardRef<HTMLDivElement, LowerCardContentDetailsProps>(({ build }, ref) => {
  if (build.triggerParams?.priority === undefined || build.triggerParams.priority === 0) return null;

  return (
    <BitkitTooltip text={`Priority: ${build.triggerParams.priority}`}>
      <BuildMetaData icon={IconFlag} gap="0" minWidth="0" ref={ref} zIndex={1} color="text/secondary">
        <Text textStyle="body/md/regular" paddingInlineStart="4">
          {build.triggerParams.priority > 0 ? 'High' : 'Low'}
        </Text>
      </BuildMetaData>
    </BitkitTooltip>
  );
});
BuildPriority.displayName = 'BuildPriority';

const AIRunNames = {
  'code-reviewer': 'Code reviewer',
  'build-fixer': 'Build fixer',
  'build-fixer-multiturn': 'Multi Turn Fixer',
  'ci-config-expert': 'CI config expert',
};

const AIBadge = ({ build }: LowerCardContentDetailsProps) => {
  if (!build.aiRunType) {
    return null;
  }

  return (
    <Box
      background="linear-gradient(to right, #55CEC2, #99D1F5, #9247C2, #FFB766)"
      zIndex={1}
      width="fit-content"
      borderRadius="4"
      display="flex"
      padding="1"
    >
      <Box
        background="linear-gradient(90deg, #E9FBF9, #FFFFFF)"
        borderRadius={rem(3)}
        width="fit-content"
        padding="4"
        paddingInlineEnd="8"
        display="flex"
        alignItems="center"
        gap="4"
        textStyle="comp/tag/sm"
      >
        <IconMagicWand color="icon/secondary" size="16" flexShrink={0} />
        {AIRunNames[build.aiRunType]}
      </Box>
    </Box>
  );
};

type BuildListItemLowerSectionProps = {
  build: Build;
  showBuildNumberOnMobile: boolean;
};
const BuildItemLowerSection = ({ build, showBuildNumberOnMobile }: BuildListItemLowerSectionProps) => {
  const { isMobile } = useResponsive();
  const hasCaching = build.buildToolInvocations && build.buildToolInvocations[0];

  return (
    <Box display="flex" flexDirection={['column', 'row']}>
      <BuildTagOrBranches build={build} />
      <BuildWorkflowName build={build} />
      <BuildUtilityBuild build={build} />
      <Box
        display="flex"
        flexDirection="row"
        flexShrink={0}
        flexWrap={['wrap', 'nowrap']}
        alignItems="center"
        justifyContent={['flex-start', 'flex-end']}
        gap="12"
        paddingTop="8"
        paddingRight="24"
        paddingBottom="8"
        paddingLeft="12"
        flexGrow={1}
        borderTop={['1px solid', 'none']}
        borderTopColor="border/minimal"
      >
        {hasCaching && (
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent={['flex-start', 'flex-end']}
            gap="12"
            flexGrow={1}
          >
            {build.buildToolInvocations && build.buildToolInvocations[0] && (
              <BitkitTooltip text="Cache connected">
                <BuildMetaData icon={IconBuildCache} gap={0} color="text/secondary" zIndex={1} />
              </BitkitTooltip>
            )}
          </Box>
        )}
        <AIBadge build={build} />
        <BuildPriority build={build} />
        <BuildStartedAt build={build} />
        <BuildDuration build={build} />
        <BuildCredits build={build} />
        {(!isMobile || showBuildNumberOnMobile) && <BuildNumber build={build} />}
      </Box>
    </Box>
  );
};

export default BuildItemLowerSection;
